// unmanaged
// A company's account panel (docs/project-tracking.md): the SOP's onboarding
// checklist with hints from real activity, renewal, usage since activation,
// and its open projects.
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import apiClient from "../utils/apiClient";
import { RenewalChip, formatTime } from "./projectParts";

const companyRequest = (action, payload) => apiClient.post("/company-request", { action, ...payload });

function Section({ title, children }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.6, color: "#64748B", mb: 0.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function AccountDialog({ companyId, onClose, onChanged }) {
  const [account, setAccount] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setAccount((await companyRequest("account", { id: companyId })).records[0]);
    } catch (e) {
      setError(e?.message || "Could not load the account");
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) load();
  }, [companyId, load]);

  const toggle = async (step, completed) => {
    try {
      setAccount((await companyRequest("set_onboarding", { id: companyId, step, completed })).records[0]);
      onChanged?.();
    } catch (e) {
      setError(e?.message || "That didn't save");
    }
  };

  const usage = account?.usage;
  return (
    <Dialog open={!!companyId} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{account?.name || "Account"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
        {!account ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <Section title={`Onboarding · ${account.onboarding_done} of ${account.onboarding.length}`}>
              {account.onboarding.map((s) => (
                <Box key={s.key} sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Checkbox
                    checked={!!s.completed_at}
                    onChange={(e) => toggle(s.key, e.target.checked)}
                    inputProps={{ "aria-label": s.label }}
                    sx={{ p: 0.75 }}
                  />
                  <Box sx={{ pt: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: s.completed_at ? 600 : 400 }}>{s.label}</Typography>
                    {s.completed_at ? (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {formatTime(s.completed_at)} · {s.completed_by}
                      </Typography>
                    ) : s.hint ? (
                      <Typography variant="caption" sx={{ color: "#2563EB" }}>
                        Hint: {s.hint.text} {formatTime(s.hint.at)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>Not yet</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Section>
            <Section title="Renewal">
              {account.renewal_date ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography variant="body2">{new Date(account.renewal_date).toISOString().slice(0, 10)}</Typography>
                  <RenewalChip days={account.renewal_days} />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>No renewal date. Set it in Edit.</Typography>
              )}
            </Section>
            <Section title={usage?.since ? `Usage since activation (${formatTime(usage.since)})` : "Usage, all time"}>
              <Typography variant="body2">
                {usage.users} user{usage.users === 1 ? "" : "s"} · {usage.uploads} upload{usage.uploads === 1 ? "" : "s"} ·{" "}
                {usage.completed_analyses} completed analys{usage.completed_analyses === 1 ? "is" : "es"}
              </Typography>
            </Section>
            <Section title="Open projects">
              {account.projects.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>None.</Typography>
              ) : account.projects.map((p) => (
                <Box key={p.id}>
                  <Link component={RouterLink} to={`/projects/${encodeURIComponent(p.id)}`}>{p.id}</Link> {p.name}
                </Box>
              ))}
            </Section>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
